/**
 * AccountSelector Component
 *
 * A multi-select component for choosing target social accounts in automation rules.
 * Shows checkboxes for each connected Facebook Page account.
 *
 * Features:
 * - Shows loading skeleton while fetching accounts
 * - Displays empty state when no accounts are connected
 * - Visual feedback for selected accounts
 * - Disabled state for accounts that are not active
 */

import { memo, useCallback } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';
import type { IAccountSelectorProps } from '@/@types/automation';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Facebook, AlertCircle } from 'lucide-react';

/**
 * Connected social account type (from listConnectedAccounts query)
 */
interface IConnectedAccount {
  _id: Id<'connectedSocialAccounts'>;
  _creationTime: number;
  userId: Id<'users'>;
  platform: 'facebook_page';
  accountId: string;
  accountName: string;
  accountUsername?: string;
  accountAvatarUrl?: string;
  accountCategory?: string;
  accountFollowerCount?: number;
  status: 'active' | 'expired' | 'invalid' | 'reconnect_required';
  totalPublishedPosts: number;
  lastPublishedAt?: number;
  connectedAt: number;
  updatedAt: number;
}

export const AccountSelector = memo(function AccountSelector({
  selectedAccountIds,
  onChange,
  className,
}: IAccountSelectorProps) {
  // Fetch connected accounts
  const accounts = useQuery(api.socialMedia.accounts.listConnectedAccounts);
  const isLoading = accounts === undefined;

  const handleToggle = useCallback(
    (accountId: Id<'connectedSocialAccounts'>) => {
      const newSelection = [...selectedAccountIds];
      const index = newSelection.findIndex(id => id === accountId);

      if (index >= 0) {
        newSelection.splice(index, 1);
      } else {
        newSelection.push(accountId);
      }

      onChange(newSelection);
    },
    [selectedAccountIds, onChange]
  );

  if (isLoading) {
    return (
      <div className={cn('space-y-3', className)}>
        {['skeleton-account-a', 'skeleton-account-b'].map(key => (
          <div key={key} className="flex items-center gap-3 p-3 rounded-md border">
            <Skeleton className="h-5 w-5 rounded" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!accounts || accounts.length === 0) {
    return (
      <div
        className={cn(
          'flex flex-col items-center gap-2 py-6 text-center border rounded-md bg-muted/30',
          className
        )}
      >
        <AlertCircle className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">No connected accounts found.</p>
        <p className="text-xs text-muted-foreground">
          Connect a Facebook Page first to use automation.
        </p>
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      {accounts.map((account: IConnectedAccount) => {
        const isSelected = selectedAccountIds.includes(account._id);
        const isDisabled = account.status !== 'active';

        return (
          <div
            key={account._id}
            className={cn(
              'flex items-start gap-3 p-3 rounded-md border transition-colors',
              isSelected && 'border-primary bg-primary/5',
              isDisabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            <Checkbox
              id={`account-${account._id}`}
              checked={isSelected}
              onCheckedChange={() => handleToggle(account._id)}
              disabled={isDisabled}
              className="mt-0.5"
            />
            <div className="flex-1 min-w-0">
              <Label
                htmlFor={`account-${account._id}`}
                className={cn(
                  'flex items-center gap-2 cursor-pointer font-medium',
                  isDisabled && 'cursor-not-allowed'
                )}
              >
                <Facebook className="h-4 w-4 text-blue-600 shrink-0" />
                <span className="truncate">{account.accountName || account.accountId}</span>
                {isDisabled && (
                  <span className="text-xs text-destructive shrink-0">({account.status})</span>
                )}
              </Label>

              {/* Account metadata */}
              <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                {account.accountUsername && <span>@{account.accountUsername}</span>}
                {account.accountCategory && (
                  <span className="truncate">{account.accountCategory}</span>
                )}
                {account.accountFollowerCount !== undefined && account.accountFollowerCount > 0 && (
                  <span>{formatFollowerCount(account.accountFollowerCount)} followers</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
});

/**
 * Format follower count with K/M abbreviations
 */
function formatFollowerCount(count: number): string {
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1)}M`;
  }
  if (count >= 1_000) {
    return `${(count / 1_000).toFixed(1)}K`;
  }
  return count.toString();
}
